import React, { useEffect, useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Lighthouse, Visit } from '../types';
import '../styles/Admin.css';

interface Member {
  id: string;
  email: string;
  member_until: string | null;
  first_name: string | null;
  welcomed: boolean;
}

interface PendingVisit extends Visit {
  users: { email: string } | null;
  lighthouses: { name: string } | null;
  visit_photos: { id: number; url: string }[] | null;
}

function isActive(m: Member) {
  return !!m.member_until && new Date(m.member_until) > new Date();
}

function AdminPage() {
  const [members, setMembers] = useState<Member[]>([]);
  const [stampCounts, setStampCounts] = useState<Record<string, number>>({});
  const [lighthouses, setLighthouses] = useState<Lighthouse[]>([]);
  const [memberId, setMemberId] = useState('');
  const [memberVisits, setMemberVisits] = useState<Visit[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [state, setState] = useState('All');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [pending, setPending] = useState<PendingVisit[]>([]);
  const [pendingSel, setPendingSel] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [notesSearch, setNotesSearch] = useState('');
  const [notesLhId, setNotesLhId] = useState<number | ''>('');
  const [notesText, setNotesText] = useState('');
  const [notesBusy, setNotesBusy] = useState(false);
  const [notesMessage, setNotesMessage] = useState('');

  const loadPending = async () => {
    const { data } = await supabase
      .from('visits')
      .select('*, users(email), lighthouses(name), visit_photos(id, url)')
      .eq('verified', false)
      .order('visited_at', { ascending: false });
    setPending((data as PendingVisit[]) || []);
  };

  const loadStampCounts = async () => {
    const { data } = await supabase.from('visits').select('user_id').eq('verified', true);
    const counts: Record<string, number> = {};
    (data || []).forEach((v: { user_id: string }) => {
      counts[v.user_id] = (counts[v.user_id] || 0) + 1;
    });
    setStampCounts(counts);
  };

  useEffect(() => {
    supabase
      .from('users')
      .select('id, email, member_until, first_name, welcomed')
      .order('email')
      .then(({ data }) => setMembers((data as Member[]) || []));
    supabase
      .from('lighthouses')
      .select('*')
      .order('name')
      .then(({ data }) => setLighthouses(data || []));
    loadPending();
    loadStampCounts();
  }, []);

  useEffect(() => {
    setSelected(new Set());
    setMessage('');
    if (!memberId) {
      setMemberVisits([]);
      return;
    }
    supabase
      .from('visits')
      .select('*')
      .eq('user_id', memberId)
      .then(({ data }) => setMemberVisits(data || []));
  }, [memberId]);

  const stampedIds = useMemo(
    () => new Set(memberVisits.map((v) => v.lighthouse_id)),
    [memberVisits]
  );
  const states = useMemo(
    () => ['All', ...Array.from(new Set(lighthouses.map((l) => l.state))).sort()],
    [lighthouses]
  );
  const shown = lighthouses.filter(
    (l) =>
      (state === 'All' || l.state === state) &&
      l.name.toLowerCase().includes(search.toLowerCase())
  );

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      const aActive = isActive(a) ? 0 : 1;
      const bActive = isActive(b) ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      return a.email.localeCompare(b.email);
    });
  }, [members]);

  const toggleWelcomed = async (m: Member) => {
    const next = !m.welcomed;
    setMembers((prev) =>
      prev.map((x) => (x.id === m.id ? { ...x, welcomed: next } : x))
    );
    const { error } = await supabase
      .from('users')
      .update({ welcomed: next })
      .eq('id', m.id);
    if (error) {
      setMembers((prev) =>
        prev.map((x) => (x.id === m.id ? { ...x, welcomed: m.welcomed } : x))
      );
    }
  };

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const stampSelected = async () => {
    if (!memberId || selected.size === 0) return;
    setBusy(true);
    setMessage('');
    const rows = Array.from(selected).map((lighthouse_id) => ({
      user_id: memberId,
      lighthouse_id,
      visited_at: new Date(`${date}T12:00:00`).toISOString(),
      verified: true,
    }));
    const { error } = await supabase
      .from('visits')
      .upsert(rows, { onConflict: 'user_id,lighthouse_id', ignoreDuplicates: true });
    if (error) {
      setMessage(`Could not stamp: ${error.message}`);
    } else {
      setMessage(`Stamped ${rows.length} lighthouse${rows.length === 1 ? '' : 's'}.`);
      const { data } = await supabase.from('visits').select('*').eq('user_id', memberId);
      setMemberVisits(data || []);
      setSelected(new Set());
      loadStampCounts();
    }
    setBusy(false);
  };

  const removeStamp = async (lighthouseId: number) => {
    const visit = memberVisits.find((v) => v.lighthouse_id === lighthouseId);
    if (!visit || !window.confirm('Remove this stamp?')) return;
    const { error } = await supabase.from('visits').delete().eq('id', visit.id);
    if (!error) {
      setMemberVisits((prev) => prev.filter((v) => v.id !== visit.id));
      loadStampCounts();
    }
  };

  const pickNotesLighthouse = (id: number | '') => {
    setNotesLhId(id);
    setNotesMessage('');
    const lh = lighthouses.find((l) => l.id === id);
    setNotesText(lh?.access_notes || '');
  };

  const saveNotes = async () => {
    if (!notesLhId) return;
    setNotesBusy(true);
    setNotesMessage('');
    const trimmed = notesText.trim();
    const { error } = await supabase
      .from('lighthouses')
      .update({ access_notes: trimmed || null })
      .eq('id', notesLhId);
    if (error) {
      setNotesMessage(`Could not save: ${error.message}`);
    } else {
      setLighthouses((prev) =>
        prev.map((l) =>
          l.id === notesLhId ? { ...l, access_notes: trimmed || null } : l
        )
      );
      setNotesMessage('Saved. Members see it on the lighthouse page now.');
    }
    setNotesBusy(false);
  };

  const togglePending = (id: number) => {
    const next = new Set(pendingSel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPendingSel(next);
  };

  const selectAllPending = () => {
    if (pendingSel.size === pending.length) setPendingSel(new Set());
    else setPendingSel(new Set(pending.map((p) => p.id)));
  };

  const approveSelected = async () => {
    if (pendingSel.size === 0) return;
    if (!window.confirm(`Approve ${pendingSel.size} check-in${pendingSel.size === 1 ? '' : 's'}?`)) return;
    const ids = Array.from(pendingSel);
    const { error } = await supabase.from('visits').update({ verified: true }).in('id', ids);
    if (!error) {
      setPending((prev) => prev.filter((p) => !pendingSel.has(p.id)));
      setPendingSel(new Set());
      loadStampCounts();
    }
  };

  const approve = async (v: PendingVisit) => {
    const { error } = await supabase.from('visits').update({ verified: true }).eq('id', v.id);
    if (!error) {
      setPending((prev) => prev.filter((p) => p.id !== v.id));
      loadStampCounts();
    }
  };
  const reject = async (v: PendingVisit) => {
    if (!window.confirm('Reject and delete this check-in?')) return;
    const { error } = await supabase.from('visits').delete().eq('id', v.id);
    if (!error) setPending((prev) => prev.filter((p) => p.id !== v.id));
  };

  const currentMember = members.find((m) => m.id === memberId);
  const activeCount = members.filter(
    (m) => isActive(m) && new Date(m.member_until as string).getFullYear() < 2099
  ).length;

  return (
    <div className="page admin">
      <h1>Admin</h1>

      <section className="admin-block">
        <h2>
          Members <span className="pill">{activeCount} paid</span>
        </h2>
        <ul className="pending-list">
          {sortedMembers.map((m) => (
            <li key={m.id} className="pending-row">
              <div className="pending-text">
                <p className="stamp-name">
                  {m.first_name || m.email.split('@')[0]}
                  {isActive(m) ? ' · Paid' : ''}
                </p>
                <p className="stamp-meta">
                  {m.email} · {stampCounts[m.id] || 0} stamp
                  {(stampCounts[m.id] || 0) === 1 ? '' : 's'}
                  {isActive(m) && m.member_until
                    ? ` · until ${new Date(m.member_until).toLocaleDateString('en-AU')}`
                    : ' · Not a member yet'}
                </p>
              </div>
              <label className="stamp-meta" style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={m.welcomed}
                  onChange={() => toggleWelcomed(m)}
                />
                Welcomed
              </label>
            </li>
          ))}
        </ul>
      </section>

      <section className="admin-block">
        <h2>Pending check-ins {pending.length > 0 && <span className="pill">{pending.length}</span>}</h2>
        {pending.length === 0 ? (
          <p className="admin-muted">Nothing waiting.</p>
        ) : (
          <>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
            <button className="link-btn" onClick={selectAllPending}>
              {pendingSel.size === pending.length ? 'Clear selection' : 'Select all'}
            </button>
            {pendingSel.size > 0 && (
              <button className="btn-primary" style={{ padding: '8px 14px' }} onClick={approveSelected}>
                Approve {pendingSel.size} selected
              </button>
            )}
          </div>
          <ul className="pending-list">
            {pending.map((v) => (
              <li key={v.id} className="pending-row">
                <input
                  type="checkbox"
                  checked={pendingSel.has(v.id)}
                  onChange={() => togglePending(v.id)}
                  style={{ marginRight: '8px' }}
                />
                <div className="pending-text">
                  <p className="stamp-name">{v.lighthouses?.name || `Lighthouse #${v.lighthouse_id}`}</p>
                  <p className="stamp-meta">
                    {v.users?.email} · {new Date(v.visited_at).toLocaleDateString('en-AU')}
                  </p>
                  {(v.visit_photos || []).length > 0 ? (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        marginTop: '8px',
                      }}
                    >
                      {(v.visit_photos || []).map((p) => (
                        <a
                          key={p.id}
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={p.url}
                            alt="Member check-in photo"
                            style={{
                              width: '72px',
                              height: '72px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              display: 'block',
                            }}
                          />
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="stamp-meta">No photo attached</p>
                  )}
                </div>
                <button className="icon-btn approve" onClick={() => approve(v)} aria-label="Approve">
                  <Check size={18} />
                </button>
                <button className="icon-btn reject" onClick={() => reject(v)} aria-label="Reject">
                  <X size={18} />
                </button>
              </li>
            ))}
          </ul>
          </>
        )}
      </section>

      <section className="admin-block">
        <h2>
          Getting there notes{' '}
          <span className="pill">
            {lighthouses.filter((l) => l.access_notes && l.access_notes.trim()).length} of {lighthouses.length} written
          </span>
        </h2>
        <div className="admin-controls">
          <input
            type="text"
            placeholder="Search lighthouses"
            value={notesSearch}
            onChange={(e) => setNotesSearch(e.target.value)}
          />
        </div>
        <select
          value={notesLhId}
          onChange={(e) => pickNotesLighthouse(Number(e.target.value) || '')}
        >
          <option value="">Select a lighthouse</option>
          {lighthouses
            .filter((l) => l.name.toLowerCase().includes(notesSearch.toLowerCase()))
            .map((l) => (
              <option key={l.id} value={l.id}>
                {l.access_notes && l.access_notes.trim() ? '\u2713 ' : ''}{l.name} ({l.state})
              </option>
            ))}
        </select>
        {notesLhId && (
          <>
            <textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder="Parking, the walk in, opening times, tours, who to contact, anything a hunter needs to know."
              style={{
                width: '100%',
                minHeight: '140px',
                fontSize: '16px',
                color: '#000000',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #d7dce5',
                boxSizing: 'border-box',
                marginTop: '10px',
                fontFamily: 'inherit',
                lineHeight: 1.5,
              }}
            />
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginTop: '8px' }}>
              <button className="btn-primary" onClick={saveNotes} disabled={notesBusy}>
                {notesBusy ? 'Saving...' : 'Save notes'}
              </button>
              {notesMessage && <p className="admin-message" style={{ margin: 0 }}>{notesMessage}</p>}
            </div>
          </>
        )}
      </section>

      <section className="admin-block">
        <h2>Stamp a member's passport</h2>
        <select value={memberId} onChange={(e) => setMemberId(e.target.value)}>
          <option value="">Select a member</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.first_name ? `${m.first_name} (${m.email})` : m.email}
            </option>
          ))}
        </select>

        {currentMember && (
          <>
            <p className="admin-muted">
              {stampedIds.size} of {lighthouses.length} stamped ·{' '}
              {isActive(currentMember) ? 'Membership active' : 'No active membership'}
            </p>

            <div className="admin-controls">
              <input
                type="text"
                placeholder="Search lighthouses"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select value={state} onChange={(e) => setState(e.target.value)}>
                {states.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>

            <ul className="tick-list">
              {shown.map((l) => {
                const done = stampedIds.has(l.id);
                return (
                  <li key={l.id} className={`tick-row ${done ? 'done' : ''}`}>
                    <label>
                      <input
                        type="checkbox"
                        checked={done || selected.has(l.id)}
                        disabled={done}
                        onChange={() => toggle(l.id)}
                      />
                      <span className="tick-text">
                        <span className="stamp-name">{l.name}</span>
                        <span className="stamp-meta">
                          {l.passport_type} · {l.state}
                        </span>
                      </span>
                    </label>
                    {done && (
                      <button
                        className="link-btn"
                        onClick={() => removeStamp(l.id)}
                      >
                        remove
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="stamp-bar">
              <label className="date-label">
                Visit date
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </label>
              <button
                className="btn-primary"
                onClick={stampSelected}
                disabled={busy || selected.size === 0}
              >
                {busy ? 'Stamping...' : `Stamp ${selected.size} selected`}
              </button>
              {message && <p className="admin-message">{message}</p>}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default AdminPage;
